<template>
  <div class="container">
    <canvas ref="canvas"></canvas>
    <div v-if="showHint" class="hint">
      Slide your finger through the letters to form the word!
    </div>
    <div class="controls">
      <div class="word-attack-toggle">
        <label class="switch">
          <input type="checkbox" v-model="wordAttackActive" @change="handleWordAttackSwitchChange" />
          <span class="slider"></span>
        </label>
        <span class="switch-label">{{ wordAttackActive ? 'Word Attack On' : 'Word Attack Off' }}</span>
      </div>
      <transition name="fade">
        <div v-if="wordAttackActive && sessionStartTime" class="word-attack-info">
          <div class="session-time">Session Time Left: {{ sessionTimeLeft }} s</div>
          <div class="clue-board">Clue: {{ currentClue }}</div>
          <button @click="showHint">Hint</button>
          <div v-if="showWordHint" class="word-hint">{{ currentWord }}</div>
          <div class="progress-container">
            <div class="progress-bar" :style="{ width: questionProgress + '%' }"></div>
            <div class="progress-label">{{ questionProgress.toFixed(1) }}%</div>
          </div>
          <div class="score">Score: {{ wordAttackScore }}</div>
          <div class="highscore">High Score: {{ highScore }}</div>
          <div class="user-input">Your Answer: {{ userInput }}</div>
        </div>
      </transition>
    </div>

    <!-- Difficulty Selection Modal -->
    <transition name="modal">
      <div class="modal-overlay" v-if="showDifficultyModal">
        <div class="modal-content">
          <h2>Select Difficulty</h2>
          <div class="difficulty-selection-modal">
            <button @click="selectDifficulty('easy')">Easy (20s)</button>
            <button @click="selectDifficulty('medium')">Medium (10s)</button>
            <button @click="selectDifficulty('hard')">Hard (7.5s)</button>
            <button @click="selectDifficulty('impossible')">Impossible (5s)</button>
          </div>
        </div>
      </div>
    </transition>

    <!-- Feature Tip Modal -->
    <transition name="modal">
      <div class="modal-overlay" v-if="showFeatureModal">
        <div class="modal-content">
          <h2>Feature Tip</h2>
          <p>{{ currentTip }}</p>
          <div class="modal-buttons">
            <button @click="neverShowTips">Never show again</button>
            <button @click="closeModalTip">View later</button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>
<script>
export default {
  name: 'WordAttackPage',
  data() {
    return {
      canvas: null,
      ctx: null,
      hue: 0,
      particles: [],
      touchActive: false,
      animationFrame: null,
      audioContext: null,
      lastSoundTime: 0,
      gravityX: 0,
      gravityY: 0,
      // Word Attack properties
      wordAttackActive: false,
      wordAttackLevel: 'easy',
      sessionDuration: 120, // in seconds
      questionDuration: 0,
      sessionStartTime: null,
      questionStartTime: null,
      wordAttackScore: 0,
      wordTimerInterval: null,
      highScore: 0,
      currentTime: Date.now(),
      questionDelay: 1000, // 1-second delay between questions
      // Word question properties
      currentClue: '',
      currentWord: '',
      userInput: '',
      bubbleLetters: [],
      // Bubble properties
      bubbles: [],
      bubbleRadius: 40,
      loadingBubbles: false,
      // Used words tracker (to avoid repeats in one session)
      usedWords: [],
      // Hint properties
      // (Each bubble will get a “hintActive” flag when highlighted)
      // Touch input helper to allow reselecting the same bubble after leaving it.
      lastBubble: null,
      // Modal Tip properties
      showFeatureModal: false,
      neverShowModal: false,
      modalContent: [
        "Tip: Slide your finger through the letters to form the word.",
        "Tip: Use the hint button to see the correct letters light up one by one.",
        "Tip: Each correct word adds to your score.",
        "Tip: Enjoy the cosmic visuals while forming words."
      ],
      modalIndex: 0,
      modalInterval: null,
      // Difficulty Modal property
      showDifficultyModal: false,
      // Celebration properties
      celebrationActive: false,
      celebrationParticles: [],
      // New Word list – 200 funny, healthy, relationship/sex–related words!
      wordList: [
        { word: 'flirt', clue: 'Playful tease that sparks smiles.' },
        { word: 'cuddle', clue: 'Cozy embrace full of warmth.' },
        { word: 'passion', clue: 'Burning desire that lights up the night.' },
        { word: 'romance', clue: 'Heartfelt moments under starry skies.' },
        { word: 'intimacy', clue: 'Deep connection that brings souls together.' },
        { word: 'desire', clue: 'Yearning that ignites playful sparks.' },
        { word: 'affection', clue: 'Gentle care and loving hugs.' },
        { word: 'tease', clue: 'Light-hearted banter with a wink.' },
        { word: 'spark', clue: 'That little flash of excitement.' },
        { word: 'smile', clue: 'Radiant expression of joy.' },
        { word: 'bond', clue: 'Connection that feels like home.' },
        { word: 'embrace', clue: 'Warm hug that melts hearts.' },
        { word: 'kissing', clue: 'Sweet lips meeting in playful delight.' },
        { word: 'smooch', clue: 'A cheeky peck full of charm.' },
        { word: 'harmony', clue: 'Balanced love and mutual respect.' },
        { word: 'chemistry', clue: 'Magnetic energy between two souls.' },
        { word: 'adore', clue: 'Cherishing every moment together.' },
        { word: 'cherish', clue: 'Valuing love in its purest form.' },
        { word: 'playful', clue: 'Light-hearted fun in every glance.' },
        { word: 'sultry', clue: 'Steamy vibes with a cool twist.' },
        { word: 'sizzle', clue: 'Heat that turns moments into memories.' },
        { word: 'giggle', clue: 'Joyful laughter that bonds hearts.' },
        { word: 'laughter', clue: 'Medicine for a happy relationship.' },
        { word: 'romantic', clue: 'Dreamy moments that make hearts flutter.' },
        { word: 'snuggle', clue: 'Cozy closeness on a chilly night.' },
        { word: 'sweetheart', clue: 'Beloved partner full of love.' },
        { word: 'devotion', clue: 'Unwavering commitment and care.' },
        { word: 'flaming', clue: 'A fiery passion that cannot be contained.' },
        { word: 'zest', clue: 'Energetic spice that enlivens romance.' },
        { word: 'fervor', clue: 'Intense enthusiasm in every touch.' },
        { word: 'bliss', clue: 'Pure joy found in loving moments.' },
        { word: 'euphoria', clue: 'Exhilaration of deep connection.' },
        { word: 'ecstasy', clue: 'Peak moments of passionate joy.' },
        { word: 'amour', clue: 'French flair for romantic adventures.' },
        { word: 'sincere', clue: 'Genuine care that speaks volumes.' },
        { word: 'vibrant', clue: 'Full of life and sparkling energy.' },
        { word: 'lusty', clue: 'Robust desire with a playful edge.' },
        { word: 'allure', clue: 'Magnetic charm that draws you in.' },
        { word: 'tempt', clue: 'To entice with subtle suggestions.' },
        { word: 'seduce', clue: 'To allure with captivating gestures.' },
        { word: 'fascinate', clue: 'Holding attention with enchanting grace.' },
        { word: 'enchant', clue: 'To cast a spell with genuine charm.' },
        { word: 'ravish', clue: 'Overwhelming passion in every encounter.' },
        { word: 'caress', clue: 'Gentle touches that speak of love.' },
        { word: 'fondness', clue: 'Warm, tender feelings that endure.' },
        { word: 'smolder', clue: 'Quiet intensity that burns slowly.' },
        { word: 'fancy', clue: 'To delight in every charming detail.' },
        { word: 'infatuation', clue: 'A whimsical, head-over-heels crush.' },
        { word: 'admire', clue: 'Respect mixed with passionate awe.' },
        { word: 'appreciate', clue: 'Grateful recognition of love’s beauty.' },
        { word: 'joyful', clue: 'Overflowing happiness in each encounter.' },
        { word: 'blushing', clue: 'The sweet color of tender affection.' },
        { word: 'warmth', clue: 'The cozy heat of mutual care.' },
        { word: 'elegance', clue: 'Graceful charm in every loving gesture.' },
        { word: 'zany', clue: 'Fun, quirky moments that make love unique.' },
        { word: 'quirky', clue: 'Delightfully unusual and endearing.' },
        { word: 'cheer', clue: 'Uplifting joy that lightens every mood.' },
        { word: 'mirth', clue: 'Happiness that bubbles up from within.' },
        { word: 'delight', clue: 'Pure pleasure in shared smiles.' },
        { word: 'affable', clue: 'Easy-going and friendly in love.' },
        { word: 'charisma', clue: 'Magnetic personality that captivates.' },
        { word: 'companion', clue: 'A partner in both fun and life.' },
        { word: 'devoted', clue: 'Loyal love that stands the test of time.' },
        { word: 'romp', clue: 'Light-hearted, playful frolic together.' },
        { word: 'frolic', clue: 'Joyfully playful moments with your love.' },
        { word: 'zestful', clue: 'Full of lively energy and passion.' },
        { word: 'spicy', clue: 'A dash of heat to keep things interesting.' },
        { word: 'saucy', clue: 'Cheekily bold and full of spirit.' },
        { word: 'lively', clue: 'Active and full of energetic charm.' },
        { word: 'vivid', clue: 'Bright, clear, and strikingly passionate.' },
        { word: 'effervescent', clue: 'Bubbly enthusiasm that lifts spirits.' },
        { word: 'bubbly', clue: 'Cheerful and effusive in every gesture.' },
        { word: 'sparkle', clue: 'A glitter of excitement in every glance.' },
        { word: 'radiant', clue: 'Shining brightly with inner love.' },
        { word: 'daring', clue: 'Bold moves that take love to new heights.' },
        { word: 'risqué', clue: 'Suggestively playful in a tasteful way.' },
        { word: 'bold', clue: 'Fearlessly passionate and genuine.' },
        { word: 'naughty', clue: 'Mischievously fun with a wink.' },
        { word: 'playmate', clue: 'A partner in every playful adventure.' },
        { word: 'sassy', clue: 'Spirited and cheekily confident.' },
        { word: 'perky', clue: 'Lively and full of delightful energy.' },
        { word: 'charming', clue: 'Exuding attractive appeal effortlessly.' },
        { word: 'exotic', clue: 'Intriguingly different and alluring.' },
        { word: 'vivacious', clue: 'Full of life and bubbly enthusiasm.' },
        { word: 'enticing', clue: 'Attractive and tempting in every way.' },
        { word: 'luscious', clue: 'Richly appealing and deliciously tempting.' },
        { word: 'tempting', clue: 'Irresistibly inviting and alluring.' },
        { word: 'inviting', clue: 'Warm and welcoming, drawing you close.' },
        { word: 'tantalize', clue: 'Teasingly attractive and hard to resist.' },
        { word: 'magnetic', clue: 'Drawing you in with irresistible force.' },
        { word: 'alluring', clue: 'Seductively charming in every detail.' },
        { word: 'smitten', clue: 'Deeply in love at first glance.' },
        { word: 'adorable', clue: 'Lovable in a sweet, endearing way.' },
        { word: 'loving', clue: 'Full of care and tender emotions.' },
        { word: 'zippy', clue: 'Quick, spirited, and full of zest.' },
        { word: 'spunky', clue: 'Courageously full of lively spirit.' },
        { word: 'frolicsome', clue: 'Playfully spirited in every moment.' },
        { word: 'mellow', clue: 'Smooth, relaxed, and warmly inviting.' },
        { word: 'gentle', clue: 'Soft and kind in every affectionate touch.' },
        { word: 'intimate', clue: 'Close, personal, and heartwarmingly real.' },
        { word: 'caring', clue: 'Showing kindness and thoughtful concern.' },
        { word: 'compassion', clue: 'Deep empathy and loving understanding.' },
        { word: 'relish', clue: 'Enjoying every sweet moment together.' },
        { word: 'savor', clue: 'Taking pleasure in every tender touch.' },
        { word: 'tasteful', clue: 'Elegant and refined in loving gestures.' },
        { word: 'lust', clue: 'Intense desire that sparks passion.' },
        { word: 'eager', clue: 'Keen and enthusiastic in every embrace.' },
        { word: 'crave', clue: 'Deep longing for that special connection.' },
        { word: 'longing', clue: 'Yearning that fills every heartbeat.' },
        { word: 'yearn', clue: 'Deep desire for a heartfelt connection.' },
        { word: 'desirous', clue: 'Eagerly seeking intimate closeness.' },
        { word: 'rousing', clue: 'Stirring excitement in every moment.' },
        { word: 'stimulate', clue: 'To spark energy and loving sensations.' },
        { word: 'ignite', clue: 'To set passion aflame with a touch.' },
        { word: 'arouse', clue: 'To awaken desire in a gentle way.' },
        { word: 'excite', clue: 'To fill with anticipation and joy.' },
        { word: 'whimsy', clue: 'Light-hearted fun with a touch of magic.' },
        { word: 'fluffy', clue: 'Soft, cuddly, and irresistibly cute.' },
        { word: 'snazzy', clue: 'Stylishly attractive and fun-loving.' },
        { word: 'zestiness', clue: 'A burst of lively, spirited energy.' },
        { word: 'spirit', clue: 'The lively essence of a loving bond.' },
        { word: 'glee', clue: 'Joyful exuberance in shared laughter.' },
        { word: 'chirpy', clue: 'Cheerful and full of bright energy.' },
        { word: 'jolly', clue: 'Merrily fun and full of hearty laughs.' },
        { word: 'mirthful', clue: 'Overflowing with cheerful amusement.' },
        { word: 'buoyant', clue: 'Light-hearted and spirited in love.' },
        { word: 'sunnyside', clue: 'Always looking on the bright side of romance.' },
        { word: 'beaming', clue: 'Radiating happiness and loving warmth.' },
        { word: 'effusive', clue: 'Expressing feelings in a warm, heartfelt way.' },
        { word: 'breezy', clue: 'Casually cool and effortlessly charming.' },
        { word: 'lithe', clue: 'Gracefully flexible and beautifully fluid.' },
        { word: 'animated', clue: 'Full of life, energy, and joyous spark.' },
        { word: 'vivacity', clue: 'A spirited zest that makes hearts race.' },
        { word: 'sparkly', clue: 'Glittering with lively, vibrant energy.' },
        { word: 'twinkle', clue: 'A small, shining light of affection.' },
        { word: 'smoldering', clue: 'Intense and quietly passionate.' },
        { word: 'sultriness', clue: 'A subtly seductive, hot allure.' },
        { word: 'fervid', clue: 'Intensely enthusiastic and passionate.' },
        { word: 'ardent', clue: 'Strongly passionate and deeply sincere.' },
        { word: 'hearty', clue: 'Robust love that feels wholesome and full.' },
        { word: 'robust', clue: 'Strong, vigorous, and full of life.' },
        { word: 'vigorous', clue: 'Energetically passionate and lively.' },
        { word: 'spirituous', clue: 'Full of spirited energy and warmth.' },
        { word: 'enliven', clue: 'To make lively and infuse with passion.' },
        { word: 'bountiful', clue: 'Abundant love that overflows with joy.' },
        { word: 'racy', clue: 'Lively, spirited, and a bit cheeky.' },
        { word: 'jaunty', clue: 'Brisk, lively, and full of playful charm.' },
        { word: 'merry', clue: 'Joyfully light-hearted and fun.' },
        { word: 'jocular', clue: 'Humorously playful and endearingly witty.' },
        { word: 'spritely', clue: 'Full of energy and lively charm.' },
        { word: 'sugary', clue: 'Sweet, delightful, and heartwarming.' },
        { word: 'honeyed', clue: 'Richly sweet with tender care.' },
        { word: 'mellowed', clue: 'Smooth, gentle, and deeply relaxing.' },
        { word: 'tender', clue: 'Soft, gentle, and full of care.' },
        { word: 'amorous', clue: 'Showing deep love and desire.' },
        { word: 'lambent', clue: 'Softly glowing with affectionate light.' },
        { word: 'flicker', clue: 'A quick, playful flash of passion.' },
        { word: 'flutter', clue: 'Light, rapid, and heart-flipping excitement.' },
        { word: 'romancer', clue: 'One who weaves enchanting love stories.' },
        { word: 'dalliance', clue: 'A brief, playful, and flirtatious encounter.' },
        { word: 'mambo', clue: 'A lively, rhythmic dance of passion.' },
        { word: 'boudoir', clue: 'A private space of intimate rendezvous.' },
        { word: 'whisper', clue: 'Soft, secret words that excite the heart.' },
        { word: 'cozy', clue: 'Warm, snug, and perfectly inviting.' },
        { word: 'snug', clue: 'Compact comfort that wraps you in love.' },
        { word: 'heartfelt', clue: 'Genuine emotion that speaks to the soul.' },
        { word: 'endear', clue: 'To cause to be loved and cherished.' },
        { word: 'cherub', clue: 'Innocently sweet and full of love.' },
        { word: 'darling', clue: 'Adorably cherished and beloved.' },
        { word: 'pillow', clue: 'Soft support for sweet dreams together.' },
        { word: 'caressing', clue: 'Gentle touches that express deep care.' },
        { word: 'undulate', clue: 'To move with a graceful, rhythmic motion.' },
        { word: 'rendezvous', clue: 'A secret meeting full of anticipation.' },
        { word: 'kinky', clue: 'Playfully unconventional and adventurous.' },
        { word: 'fuzzy', clue: 'Soft, warm, and comfortingly sweet.' },
        { word: 'witty', clue: 'Clever humor that sparks connection.' },
        { word: 'ebullient', clue: 'Overflowing with cheerful enthusiasm.' },
        { word: 'piquant', clue: 'Stimulating and intriguingly spicy.' },
        { word: 'enchanting', clue: 'Utterly charming and mesmerizing.' },
        { word: 'mystical', clue: 'Magically inspiring and deeply alluring.' },
        { word: 'savoring', clue: 'Delighting in every intimate moment.' },
        { word: 'feisty', clue: 'Spirited, bold, and full of life.' },
        { word: 'kismet', clue: 'Fated connection that feels meant to be.' },
        { word: 'rapture', clue: 'Ecstatic bliss that overwhelms the senses.' },
        { word: 'libido', clue: 'The natural energy that fuels passion.' },
        { word: 'amity', clue: 'Friendly bond that deepens love.' },
        { word: 'blissful', clue: 'A state of complete happiness in togetherness.' },
        { word: 'graceful', clue: 'Moving with poise in love.' },
        { word: 'sunny', clue: 'Bright and cheerful like a summer day.' },
        { word: 'luminescent', clue: 'Shining softly with love.' },
        { word: 'vividness', clue: 'A striking clarity of heartfelt moments.' },
        { word: 'harmonious', clue: 'Balanced and sweet in every encounter.' },
        { word: 'jubilation', clue: 'Joyful celebration of shared passion.' },
        { word: 'serenade', clue: 'Musical whispers of deep affection.' },
        { word: 'fervency', clue: 'Intense zeal in every loving glance.' },
        { word: 'ardor', clue: 'Burning enthusiasm that fuels romance.' },
        { word: 'eclectic', clue: 'A mix of unique flavors in love.' },
        { word: 'moxie', clue: 'Courage and determination in romance.' },
        { word: 'sparkiness', clue: 'A lively flicker of passion.' },
        { word: 'vivify', clue: 'To enliven love with vibrant energy.' },
        { word: 'jubilee', clue: 'A festive, joyful burst of romance.' }
      ]
    };
  },
  computed: {
    sessionTimeLeft() {
      if (!this.wordAttackActive || !this.sessionStartTime) return this.sessionDuration.toFixed(1);
      let elapsed = (this.currentTime - this.sessionStartTime) / 1000;
      return Math.max(0, this.sessionDuration - elapsed).toFixed(1);
    },
    questionTimeLeft() {
      if (!this.wordAttackActive || !this.questionStartTime) return this.questionDuration.toFixed(1);
      let elapsed = (this.currentTime - this.questionStartTime) / 1000;
      return Math.max(0, this.questionDuration - elapsed).toFixed(1);
    },
    questionProgress() {
      let left = parseFloat(this.questionTimeLeft);
      if (this.questionDuration === 0) return 0;
      return ((this.questionDuration - left) / this.questionDuration) * 100;
    },
    currentTip() {
      return this.modalContent[this.modalIndex];
    }
  },
  watch: {
    wordAttackActive(newVal) {
      if (!newVal) {
        this.sessionStartTime = null;
        this.clearModalTips();
        if (!this.neverShowModal) this.scheduleModalTips();
      } else {
        this.clearModalTips();
        this.showFeatureModal = false;
      }
    },
    neverShowModal(newVal) {
      localStorage.setItem('neverShowModal', newVal);
    }
  },
  methods: {
    delay(ms) {
      return new Promise(resolve => setTimeout(resolve, ms));
    },
    resize() {
      const footerHeight = 60;
      this.canvas.width = window.innerWidth;
      this.canvas.height = window.innerHeight - footerHeight;
      if (this.wordAttackActive) {
        this.loadBubbleArrangement();
      }
    },
    animate() {
      if (!this.canvas || !this.ctx) {
        this.animationFrame = requestAnimationFrame(() => this.animate());
        return;
      }
      if (!this.celebrationActive) {
        this.ctx.fillStyle = 'rgba(0, 0, 0, 0.05)';
        this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
        for (let i = this.particles.length - 1; i >= 0; i--) {
          const particle = this.particles[i];
          particle.speedX += this.gravityX;
          particle.speedY += this.gravityY;
          this.drawParticle(particle);
          if (particle.alpha <= 0.05 || particle.size < 0.5) {
            this.particles.splice(i, 1);
          }
        }
        this.hue = (this.hue + 1) % 360;
        if (this.wordAttackActive && !this.loadingBubbles && this.bubbles.length > 0) {
          this.drawBubbles();
        }
        // Shake effect if needed
        if (this.shakeActive && this.canvas) {
          const shakeIntensity = 10;
          this.canvas.style.transform = `translate(${(Math.random() - 0.5) * shakeIntensity}px, ${(Math.random() - 0.5) * shakeIntensity}px)`;
        } else if (this.canvas) {
          this.canvas.style.transform = '';
        }
      }
      this.animationFrame = requestAnimationFrame(() => this.animate());
    },
    createParticle(x, y, pressure = 1) {
      let size = Math.random() * 15 + 5 * pressure;
      let speedX = (Math.random() * 3 - 1.5) * pressure;
      let speedY = (Math.random() * 3 - 1.5) * pressure;
      let color = `hsla(${this.hue}, 100%, 50%, 0.8)`;
      if (Math.random() < (pressure > 1.5 ? 0.05 : 0.02)) {
        color = `hsla(50, 100%, 60%, 1)`;
        size *= 1.5;
      }
      return { x, y, size, speedX, speedY, color, alpha: 1 };
    },
    drawParticle(particle) {
      this.ctx.fillStyle = particle.color;
      this.ctx.globalAlpha = particle.alpha;
      this.ctx.beginPath();
      this.ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
      this.ctx.fill();
      this.ctx.globalAlpha = 1;
      particle.x += particle.speedX;
      particle.y += particle.speedY;
      particle.alpha -= 0.02;
      particle.size *= 0.95;
    },
    createBubbleSound(frequency) {
      if (!this.audioContext) return;
      const now = this.audioContext.currentTime;
      if (now - this.lastSoundTime < 0.09) return;
      const oscillator = this.audioContext.createOscillator();
      oscillator.type = 'triangle';
      oscillator.frequency.setValueAtTime(frequency, now);
      oscillator.frequency.exponentialRampToValueAtTime(frequency * 0.5, now + 0.05);
      oscillator.detune.setValueAtTime((Math.random() - 0.5) * 20, now);
      const bufferSize = this.audioContext.sampleRate * 0.05;
      const noiseBuffer = this.audioContext.createBuffer(1, bufferSize, this.audioContext.sampleRate);
      const noiseData = noiseBuffer.getChannelData(0);
      for (let i = 0; i < bufferSize; i++) {
        noiseData[i] = Math.random() * 2 - 1;
      }
      const noiseSource = this.audioContext.createBufferSource();
      noiseSource.buffer = noiseBuffer;
      noiseSource.loop = false;
      const filter = this.audioContext.createBiquadFilter();
      filter.type = 'highpass';
      filter.frequency.setValueAtTime(frequency / 2, now);
      filter.Q.setValueAtTime(1, now);
      const gainNode = this.audioContext.createGain();
      gainNode.gain.setValueAtTime(1, now);
      gainNode.gain.exponentialRampToValueAtTime(0.001, now + 0.05);
      const merger = this.audioContext.createGain();
      oscillator.connect(merger);
      noiseSource.connect(merger);
      merger.connect(filter);
      filter.connect(gainNode);
      gainNode.connect(this.audioContext.destination);
      oscillator.start(now);
      noiseSource.start(now);
      oscillator.stop(now + 0.05);
      noiseSource.stop(now + 0.05);
      this.lastSoundTime = now;
    },
    handleDeviceOrientation(e) {
      const factor = 0.05;
      this.gravityX = ((e.gamma || 0) / 90) * factor;
      this.gravityY = ((e.beta || 0) / 90) * factor;
    },
    async loadBubbleArrangement() {
      if (this.loadingBubbles) return;
      this.loadingBubbles = true;
      this.bubbles = [];
      this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
      await this.delay(100);
      const availableShapes = ["Rectangle", "Square", "Circle", "Parallelogram", "Rhombus", "Ellipse", "Oval", "Kite", "Star"];
      const chosenShape = availableShapes[Math.floor(Math.random() * availableShapes.length)];
      const leftMargin = 50;
      const rightMargin = this.canvas.width - 50;
      const topMargin = 250;
      const bottomMargin = this.canvas.height - 50;
      const rectangleTypes = ["Rectangle", "Square", "Parallelogram", "Rhombus", "Kite"];
      const shapeSides = {
        "Triangle": 3, "Pentagon": 5, "Hexagon": 6, "Heptagon": 7, "Octagon": 8,
        "Nonagon": 9, "Decagon": 10, "Dodecagon": 12, "Tetrahedron": 3
      };
      const bubbleCount = this.bubbleLetters.length;
      const minDistance = 2 * this.bubbleRadius;
      if (rectangleTypes.includes(chosenShape)) {
        const requiredPerimeter = bubbleCount * minDistance;
        const maxWidth = rightMargin - leftMargin;
        const maxHeight = bottomMargin - topMargin;
        const perimeterRatio = requiredPerimeter / (2 * (maxWidth * 0.8 + maxHeight * 0.8));
        const scale = perimeterRatio > 1 ? 1 / perimeterRatio : 1;
        const arrWidth = maxWidth * 0.8 * scale;
        const arrHeight = maxHeight * 0.8 * scale;
        const offsetX = leftMargin + (maxWidth - arrWidth) / 2;
        const offsetY = topMargin + (maxHeight - arrHeight) / 2;
        const perimeter = 2 * (arrWidth + arrHeight);
        for (let i = 0; i < bubbleCount; i++) {
          let fraction = (i + 0.5) / bubbleCount;
          let distance = fraction * perimeter;
          let x, y;
          if (distance <= arrWidth) {
            x = offsetX + distance;
            y = offsetY;
          } else if (distance <= arrWidth + arrHeight) {
            x = offsetX + arrWidth;
            y = offsetY + (distance - arrWidth);
          } else if (distance <= 2 * arrWidth + arrHeight) {
            x = offsetX + arrWidth - (distance - (arrWidth + arrHeight));
            y = offsetY + arrHeight;
          } else {
            x = offsetX;
            y = offsetY + arrHeight - (distance - (2 * arrWidth + arrHeight));
          }
          if (y + this.bubbleRadius > this.canvas.height - 50) {
            y = this.canvas.height - 50 - this.bubbleRadius;
          }
          this.bubbles.push({
            x, y,
            radius: this.bubbleRadius,
            symbol: this.bubbleLetters[i],
            hintActive: false,
            shape: chosenShape
          });
        }
      } else if (chosenShape in shapeSides) {
        const N = shapeSides[chosenShape];
        const centerX = (leftMargin + rightMargin) / 2;
        const centerY = (topMargin + bottomMargin) / 2;
        const requiredPerimeter = bubbleCount * minDistance;
        const sideLength = requiredPerimeter / N;
        const radius = sideLength / (2 * Math.sin(Math.PI / N));
        const maxRadius = Math.min(rightMargin - leftMargin, bottomMargin - topMargin) * 0.4;
        const finalRadius = Math.min(radius, maxRadius);
        const vertices = [];
        for (let k = 0; k < N; k++) {
          let angle = (2 * Math.PI * k) / N;
          vertices.push({
            x: centerX + finalRadius * Math.cos(angle),
            y: centerY + finalRadius * Math.sin(angle)
          });
        }
        for (let i = 0; i < bubbleCount; i++) {
          let f = (i + 0.5) / bubbleCount;
          let sideIndex = Math.floor(f * N);
          let t = (f * N) - sideIndex;
          let x = (1 - t) * vertices[sideIndex].x + t * vertices[(sideIndex + 1) % N].x;
          let y = (1 - t) * vertices[sideIndex].y + t * vertices[(sideIndex + 1) % N].y;
          if (y + this.bubbleRadius > this.canvas.height - 50) {
            y = this.canvas.height - 50 - this.bubbleRadius;
          }
          this.bubbles.push({
            x, y,
            radius: this.bubbleRadius,
            symbol: this.bubbleLetters[i],
            hintActive: false,
            shape: chosenShape
          });
        }
      } else {
        const centerX = (leftMargin + rightMargin) / 2;
        const centerY = (topMargin + bottomMargin) / 2;
        const theta = 2 * Math.PI / bubbleCount;
        const requiredRadius = minDistance / theta;
        const maxRadius = Math.min(rightMargin - leftMargin, bottomMargin - topMargin) * 0.4;
        const finalRadius = Math.max(requiredRadius, maxRadius);
        for (let i = 0; i < bubbleCount; i++) {
          let angle = (2 * Math.PI / bubbleCount) * i;
          let x = centerX + finalRadius * Math.cos(angle);
          let y = centerY + finalRadius * Math.sin(angle);
          if (y + this.bubbleRadius > this.canvas.height - 50) {
            y = this.canvas.height - 50 - this.bubbleRadius;
          }
          this.bubbles.push({
            x, y,
            radius: this.bubbleRadius,
            symbol: this.bubbleLetters[i],
            hintActive: false,
            shape: chosenShape
          });
        }
      }
      this.loadingBubbles = false;
    },
    drawBubbles() {
      this.ctx.save();
      const footerLimit = this.canvas.height - 50;
      this.bubbles.forEach(bubble => {
        if (bubble.y + bubble.radius > footerLimit) {
          bubble.y = footerLimit - bubble.radius;
        }
        this.ctx.save();
        this.drawBubbleShape(bubble);
        // If this bubble is being hinted, draw its symbol in beautiful green.
        this.ctx.fillStyle = bubble.hintActive ? "#00FF00" : "#fff";
        this.ctx.font = "bold 20px sans-serif";
        this.ctx.textAlign = "center";
        this.ctx.textBaseline = "middle";
        this.ctx.fillText(bubble.symbol, bubble.x, bubble.y);
        this.ctx.restore();
      });
      this.ctx.restore();
    },
    drawBubbleShape(bubble) {
      const { x, y, radius: r, shape } = bubble;
      this.ctx.beginPath();
      const fillStyle = "rgba(0, 0, 0, 0.1)";
      const strokeStyle = "rgba(255, 255, 255, 0.5)";
      const lineWidth = 1;
      switch (shape) {
        case "Circle":
          this.ctx.arc(x, y, r, 0, Math.PI * 2);
          break;
        case "Rectangle":
        case "Square":
          this.ctx.rect(x - r, y - r, 2 * r, 2 * r);
          break;
        case "Ellipse":
          this.ctx.ellipse(x, y, r, r * 0.7, 0, 0, Math.PI * 2);
          break;
        case "Oval":
          this.ctx.ellipse(x, y, r, r * 0.6, 0, 0, Math.PI * 2);
          break;
        case "Parallelogram":
          this.ctx.moveTo(x - r, y - r * 0.5);
          this.ctx.lineTo(x + r, y - r * 0.5);
          this.ctx.lineTo(x + r * 0.8, y + r * 0.5);
          this.ctx.lineTo(x - r * 0.8, y + r * 0.5);
          break;
        case "Rhombus":
          this.ctx.moveTo(x, y - r);
          this.ctx.lineTo(x + r, y);
          this.ctx.lineTo(x, y + r);
          this.ctx.lineTo(x - r, y);
          break;
        case "Kite":
          this.ctx.moveTo(x, y - r);
          this.ctx.lineTo(x + r * 0.6, y);
          this.ctx.lineTo(x, y + r * 0.6);
          this.ctx.lineTo(x - r * 0.6, y);
          break;
        case "Star":
          this.drawStar(x, y, r, 5, r / 2);
          return;
        default:
          this.ctx.arc(x, y, r, 0, Math.PI * 2);
          break;
      }
      this.ctx.closePath();
      this.ctx.fillStyle = fillStyle;
      this.ctx.fill();
      this.ctx.lineWidth = lineWidth;
      this.ctx.strokeStyle = strokeStyle;
      this.ctx.stroke();
    },
    drawStar(cx, cy, outerRadius, points, innerRadius) {
      let angle = Math.PI / 2 * 3;
      const step = Math.PI / points;
      this.ctx.beginPath();
      this.ctx.moveTo(cx, cy - outerRadius);
      for (let i = 0; i < points; i++) {
        let x = cx + Math.cos(angle) * outerRadius;
        let y = cy + Math.sin(angle) * outerRadius;
        this.ctx.lineTo(x, y);
        angle += step;
        x = cx + Math.cos(angle) * innerRadius;
        y = cy + Math.sin(angle) * innerRadius;
        this.ctx.lineTo(x, y);
        angle += step;
      }
      this.ctx.closePath();
      this.ctx.fillStyle = "rgba(0, 0, 0, 0.1)";
      this.ctx.fill();
      this.ctx.lineWidth = 2;
      this.ctx.strokeStyle = "rgba(255, 255, 255, 0.5)";
      this.ctx.stroke();
    },
    getBubbleAt(x, y) {
      for (let i = 0; i < this.bubbles.length; i++) {
        let bubble = this.bubbles[i];
        let dx = x - bubble.x;
        let dy = y - bubble.y;
        if (Math.sqrt(dx * dx + dy * dy) <= bubble.radius) {
          return bubble;
        }
      }
      return null;
    },
    // Touch handlers now use lastBubble to allow re-selecting a bubble if the user leaves and returns.
    handleTouchStart(e) {
      e.preventDefault();
      this.touchActive = true;
      if (!this.audioContext) {
        this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
      }
      if (this.audioContext.state === 'suspended') {
        this.audioContext.resume();
      }
      if (navigator.vibrate) {
        navigator.vibrate([10, 2, 5]);
      }
      const touch = e.touches[0];
      const x = touch.clientX;
      const y = touch.clientY;
      const pressure = touch.force || 1;
      this.addParticles(x, y, pressure);
      if (this.wordAttackActive) {
        const bubble = this.getBubbleAt(x, y);
        if (bubble && this.lastBubble !== bubble) {
          this.userInput += bubble.symbol;
          this.lastBubble = bubble;
          this.createBubbleSound(400);
        }
      }
    },
    handleTouchMove(e) {
      e.preventDefault();
      if (this.touchActive) {
        const touch = e.touches[0];
        const x = touch.clientX;
        const y = touch.clientY;
        const pressure = touch.force || 1;
        this.addParticles(x, y, pressure);
        if (navigator.vibrate) {
          let intensity = Math.random();
          let vibIntensity = Math.floor(3 + intensity * 5);
          navigator.vibrate(vibIntensity);
        }
        if (this.wordAttackActive) {
          const bubble = this.getBubbleAt(x, y);
          if (bubble) {
            if (this.lastBubble !== bubble) {
              this.userInput += bubble.symbol;
              this.lastBubble = bubble;
              this.createBubbleSound(400);
            }
          } else {
            // If no bubble is touched, reset so that re‐entering a bubble counts again.
            this.lastBubble = null;
          }
        }
      }
    },
    handleTouchEnd(e) {
      e.preventDefault();
      this.touchActive = false;
      this.lastBubble = null;
      if (this.wordAttackActive && this.userInput) {
        if (this.userInput === this.currentWord) {
          this.handleCorrectAnswer();
        } else {
          this.userInput = "";
          this.createBubbleSound(100);
        }
      }
      // Play end‐touch sounds
      this.createBubbleSound(1500);
      this.createBubbleSound(800);
      if (navigator.vibrate) {
        navigator.vibrate([5, 10, 5]);
      }
    },
    addParticles(x, y, pressure = 1) {
      const count = Math.floor(5 * pressure);
      for (let i = 0; i < count; i++) {
        this.particles.push(this.createParticle(x, y, pressure));
      }
    },
    handleWordAttackSwitchChange() {
      if (this.wordAttackActive) {
        this.showDifficultyModal = true;
      } else {
        this.endWordAttack();
      }
    },
    selectDifficulty(level) {
      this.wordAttackLevel = level;
      if (level === 'easy') {
        this.questionDuration = 20;
      } else if (level === 'medium') {
        this.questionDuration = 10;
      } else if (level === 'hard') {
        this.questionDuration = 7.5;
      } else if (level === 'impossible') {
        this.questionDuration = 5;
      }
      this.showDifficultyModal = false;
      this.startWordAttack();
    },
    async startWordAttack() {
      this.wordAttackActive = true;
      this.sessionStartTime = Date.now();
      this.currentTime = Date.now();
      this.wordAttackScore = 0;
      this.userInput = '';
      this.generateWordQuestion();
      await this.loadBubbleArrangement();
      this.questionStartTime = Date.now();
      if (this.wordTimerInterval) clearInterval(this.wordTimerInterval);
      this.wordTimerInterval = setInterval(() => {
        this.currentTime = Date.now();
        let sessionElapsed = (this.currentTime - this.sessionStartTime) / 1000;
        if (sessionElapsed >= this.sessionDuration) {
          this.endWordAttack();
        } else {
          let questionElapsed = (this.currentTime - this.questionStartTime) / 1000;
          if (questionElapsed >= this.questionDuration) {
            this.handleQuestionTimeout();
          }
        }
      }, 100);
    },
    endWordAttack() {
      clearInterval(this.wordTimerInterval);
      this.wordTimerInterval = null;
      this.wordAttackActive = false;
      this.celebrateScore(this.wordAttackScore, 0);
    },
    // Now ensure the same word is not repeated in a session.
    generateWordQuestion() {
      let availableWords = this.wordList.filter(w => !this.usedWords.includes(w.word));
      if (availableWords.length === 0) {
        // Reset if all words have been used.
        this.usedWords = [];
        availableWords = this.wordList;
      }
      const wordObj = availableWords[Math.floor(Math.random() * availableWords.length)];
      this.usedWords.push(wordObj.word);
      this.currentClue = wordObj.clue;
      this.currentWord = wordObj.word;
      const letters = this.currentWord.split('');
      const extraLetters = 2;
      for (let i = 0; i < extraLetters; i++) {
        letters.push(String.fromCharCode(97 + Math.floor(Math.random() * 26)));
      }
      this.bubbleLetters = letters.sort(() => Math.random() - 0.5);
    },
    // When the hint button is pressed, light up each correct letter (in order) by setting its bubble’s hintActive flag.
    // eslint-disable-next-line
    async showHint() {
      // For each letter in the correct word, find an unhinted bubble that matches.
      for (let i = 0; i < this.currentWord.length; i++) {
        const letter = this.currentWord[i];
        const bubble = this.bubbles.find(b => b.symbol === letter && !b.hintActive);
        if (bubble) {
          bubble.hintActive = true;
          await this.delay(800);
          bubble.hintActive = false;
        }
      }
    },
    async handleCorrectAnswer() {
      this.wordAttackScore += 10;
      await this.hurrayParticles();
      if (this.wordAttackScore > this.highScore) {
        this.highScore = this.wordAttackScore;
      }
      await this.regenerateQuestion();
    },
    async handleQuestionTimeout() {
      await this.regenerateQuestion();
    },
    async regenerateQuestion() {
      await this.delay(this.questionDelay);
      let currentTime = Date.now();
      let sessionElapsed = (currentTime - this.sessionStartTime) / 1000;
      if (sessionElapsed < this.sessionDuration) {
        this.userInput = '';
        this.generateWordQuestion();
        await this.loadBubbleArrangement();
        this.questionStartTime = Date.now();
      }
    },
    async hurrayParticles() {
      this.celebrationParticles = [];
      const numParticles = 300;
      const duration = 3000;
      const startTime = Date.now();
      for (let i = 0; i < numParticles; i++) {
        this.celebrationParticles.push({
          x: this.canvas.width / 2,
          y: this.canvas.height / 2,
          vx: (Math.random() - 0.5) * 4,
          vy: (Math.random() - 0.5) * 4,
          size: Math.random() * 8 + 5,
          alpha: Math.random() * 0.5 + 0.5,
          color: `hsl(${Math.random() * 360}, 100%, 70%)`
        });
      }
      const shakeIntensity = 10;
      const shakeDuration = duration;
      const runCelebration = () => {
        this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
        if (Date.now() - startTime < shakeDuration) {
          this.canvas.style.transform = `translate(${(Math.random() - 0.5) * shakeIntensity}px, ${(Math.random() - 0.5) * shakeIntensity}px)`;
        } else {
          this.canvas.style.transform = "";
        }
        this.celebrationParticles.forEach(p => {
          p.x += p.vx;
          p.y += p.vy;
          p.vx *= 0.98;
          p.vy *= 0.98;
          p.alpha *= 0.97;
          this.ctx.fillStyle = p.color;
          this.ctx.globalAlpha = Math.max(p.alpha, 0.1);
          this.ctx.beginPath();
          this.ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
          this.ctx.fill();
        });
        if (Date.now() - startTime < duration) {
          requestAnimationFrame(runCelebration);
        } else {
          this.celebrationParticles = [];
          this.canvas.style.transform = "";
        }
      };
      runCelebration();
    },
    celebrateScore(finalScore, bonus) {
      this.celebrationActive = true;
      this.celebrationParticles = [];
      for (let i = 0; i < 250; i++) {
        this.celebrationParticles.push({
          x: Math.random() * this.canvas.width,
          y: Math.random() * this.canvas.height,
          vx: (Math.random() - 0.5) * 8,
          vy: (Math.random() - 0.5) * 8,
          size: Math.random() * 5 + 2,
          alpha: 1,
          color: `hsl(${Math.random() * 360}, 100%, 60%)`
        });
      }
      const startTime = Date.now();
      const duration = 3000;
      const runCelebration = () => {
        this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
        this.celebrationParticles.forEach(p => {
          p.x += p.vx;
          p.y += p.vy;
          p.alpha -= 0.01;
          this.ctx.fillStyle = p.color;
          this.ctx.globalAlpha = Math.max(p.alpha, 0);
          this.ctx.beginPath();
          this.ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
          this.ctx.fill();
        });
        this.ctx.globalAlpha = 1;
        this.ctx.font = 'bold 64px sans-serif';
        this.ctx.textAlign = 'center';
        this.ctx.fillStyle = '#FFD700';
        this.ctx.fillText(`Score: ${finalScore}`, this.canvas.width / 2, this.canvas.height / 2 - 20);
        this.ctx.font = 'bold 32px sans-serif';
        this.ctx.fillStyle = '#00FFFF';
        this.ctx.fillText(`Bonus: ${bonus}`, this.canvas.width / 2, this.canvas.height / 2 + 30);
        if (Date.now() - startTime < duration) {
          requestAnimationFrame(runCelebration);
        } else {
          this.celebrationActive = false;
          this.celebrationParticles = [];
        }
      };
      runCelebration();
    },
    scheduleModalTips() {
      if (this.neverShowModal) return;
      this.modalInterval = setInterval(() => {
        if (!this.wordAttackActive && !this.showFeatureModal) {
          this.showFeatureModal = true;
        }
      }, 15000);
    },
    clearModalTips() {
      clearInterval(this.modalInterval);
      this.modalInterval = null;
      this.showFeatureModal = false;
    },
    closeModalTip() {
      this.showFeatureModal = false;
    },
    neverShowTips() {
      this.neverShowModal = true;
      this.clearModalTips();
    }
  },
  mounted() {
    const stored = localStorage.getItem('neverShowModal');
    if (stored) {
      this.neverShowModal = stored === 'true';
    }
    this.canvas = this.$refs.canvas;
    this.ctx = this.canvas.getContext('2d');
    this.resize();
    window.addEventListener('resize', this.resize);
    window.addEventListener('deviceorientation', this.handleDeviceOrientation);
    this.animate();
    this.canvas.addEventListener('touchstart', this.handleTouchStart, { passive: false });
    this.canvas.addEventListener('touchmove', this.handleTouchMove, { passive: false });
    this.canvas.addEventListener('touchend', this.handleTouchEnd, { passive: false });
    setTimeout(() => { /* Initially any on-screen hint may be hidden */ }, 5000);
    if (!this.wordAttackActive && !this.neverShowModal) {
      this.scheduleModalTips();
    }
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.resize);
    window.removeEventListener('deviceorientation', this.handleDeviceOrientation);
    cancelAnimationFrame(this.animationFrame);
    this.canvas.removeEventListener('touchstart', this.handleTouchStart);
    this.canvas.removeEventListener('touchmove', this.handleTouchMove);
    this.canvas.removeEventListener('touchend', this.handleTouchEnd);
    if (this.audioContext) {
      this.audioContext.close();
    }
    this.clearModalTips();
  }
};
</script>
<style scoped>
.container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: calc(100vh - 60px);
  background: #000;
  touch-action: none;
  overflow: hidden;
}
canvas {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: calc(100vh - 60px);
  z-index: 1;
}
.hint {
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  color: rgba(255, 255, 255, 0.4);
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  font-size: 18px;
  padding: 10px 20px;
  user-select: none;
  z-index: 1000;
  pointer-events: none;
}
.controls {
  position: fixed;
  top: 10px;
  left: 10px;
  z-index: 1100;
  display: flex;
  flex-direction: column;
  gap: 15px;
}
.switch {
  position: relative;
  display: inline-block;
  width: 60px;
  height: 34px;
}
.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}
.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  transition: 0.4s;
  border-radius: 34px;
}
.slider:before {
  position: absolute;
  content: "";
  height: 26px;
  width: 26px;
  left: 4px;
  bottom: 4px;
  background-color: white;
  transition: 0.4s;
  border-radius: 50%;
}
.switch input:checked + .slider {
  background-color: #2196F3;
}
.switch input:checked + .slider:before {
  transform: translateX(26px);
}
.switch-label {
  margin-left: 10px;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  font-size: 16px;
  color: #fff;
}
.word-attack-info {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  max-width: 900px;
  margin: auto;
  padding: 20px;
  background: rgba(20, 20, 20, 0.9);
  border-radius: 10px;
  box-shadow: 0px 0px 15px rgba(0, 255, 255, 0.5);
  font-family: "Orbitron", sans-serif;
  color: #00ffff;
  text-shadow: 0 0 5px #00ffff, 0 0 10px #008080;
}
.word-attack-info > div {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.session-time {
  font-size: 12px;
  font-weight: bold;
  color: #ff6666;
  text-shadow: 0 0 5px #ff3333;
}
.clue-board {
  font-size: 20px;
  font-weight: bold;
  text-align: center;
  flex-grow: 1;
}
button {
  background: #2196F3;
  border: none;
  padding: 10px 20px;
  margin-top: 10px;
  color: #fff;
  border-radius: 5px;
  cursor: pointer;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  transition: background 0.3s;
}
button:hover {
  background: #1976D2;
}
.word-hint {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: rgba(0, 0, 0, 0.8);
  color: #fff;
  padding: 10px 20px;
  border-radius: 10px;
  font-size: 24px;
  z-index: 1000;
}
.progress-container {
  width: 100%;
  max-width: 500px;
  margin-top: 10px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 5px;
  overflow: hidden;
}
.progress-bar {
  height: 10px;
  background: linear-gradient(to right, #00ff99, #0099ff);
  transition: width 0.3s ease-in-out;
}
.progress-label {
  font-size: 10px;
  text-align: center;
  margin-top: 5px;
  color: #ffffff;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
}
.score, .highscore {
  font-size: 12px;
  font-weight: bold;
  color: #ffcc00;
  text-shadow: 0 0 5px #ffcc00;
}
.user-input {
  font-size: 28px;
  font-weight: bold;
  color: #00ff00;
  text-shadow: 0 0 10px #00ff00;
  margin-top: 20px;
}
@media (max-width: 768px) {
  .word-attack-info {
    padding: 15px;
  }
  .clue-board {
    font-size: 18px;
  }
  .user-input {
    font-size: 22px;
  }
  .score, .highscore, .session-time {
    font-size: 10px;
  }
}
@media (max-width: 480px) {
  .word-attack-info {
    flex-direction: column;
    text-align: center;
  }
  .clue-board {
    font-size: 16px;
  }
  .user-input {
    font-size: 20px;
  }
}
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.5s;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}
.modal-enter-active, .modal-leave-active {
  transition: opacity 0.3s;
}
.modal-enter-from, .modal-leave-to {
  opacity: 0;
}
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: calc(100vh - 60px);
  background: rgba(0, 0, 0, 0.7);
  z-index: 1200;
  display: flex;
  align-items: center;
  justify-content: center;
}
.modal-content {
  background: #222;
  color: #fff;
  padding: 20px 30px;
  border-radius: 10px;
  text-align: center;
  max-width: 90%;
}
.modal-content h2 {
  margin-bottom: 10px;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
}
.modal-content p {
  margin-bottom: 20px;
  font-size: 16px;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
}
.modal-buttons button, .modal-content button {
  background: #2196F3;
  border: none;
  padding: 10px 15px;
  margin: 0 5px;
  color: #fff;
  border-radius: 5px;
  cursor: pointer;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  transition: background 0.3s;
}
.modal-buttons button:hover, .modal-content button:hover {
  background: #1976D2;
}
.difficulty-selection-modal {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 15px;
}
.difficulty-selection-modal button {
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  font-size: 16px;
  padding: 8px 12px;
  background-color: #444;
  border: none;
  color: #fff;
  border-radius: 5px;
  cursor: pointer;
  transition: background 0.3s;
}
.difficulty-selection-modal button:hover {
  background-color: #666;
}
</style>
